import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '@/services/api';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Gift, Wallet, Search } from 'lucide-react';
import { toast } from 'sonner';

const REQUEST_TYPES = [
  { id: 'LOYALTY_ADD', label: 'Add loyalty points', icon: Gift, color: 'bg-amber-500', unit: 'points' },
  { id: 'LOYALTY_DEDUCT', label: 'Deduct loyalty points', icon: Gift, color: 'bg-amber-700', unit: 'points' },
  { id: 'WALLET_ADD', label: 'Add wallet credit', icon: Wallet, color: 'bg-emerald-600', unit: '₹' },
  { id: 'WALLET_DEDUCT', label: 'Wallet clawback', icon: Wallet, color: 'bg-red-600', unit: '₹' },
];

const WalletLoyaltyRequests = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [phone, setPhone] = useState('');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [myRequests, setMyRequests] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(storedUser);
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      setLoadingHistory(true);
      const res = await apiClient.get('/credit-requests');
      setMyRequests(res.data.items || []);
    } catch (e) {
      // Admin/POS may not have list permission depending on role — fail quiet
    } finally {
      setLoadingHistory(false);
    }
  };

  const resetForm = () => {
    setSelectedType(null);
    setPhone('');
    setAmount('');
    setReason('');
  };

  const handleSubmit = async () => {
    if (!phone.trim()) return toast.error('Customer phone is required');
    if (!amount || Number(amount) <= 0) return toast.error('Enter a valid amount');
    if (reason.trim().length < 5) return toast.error('Reason must be at least 5 characters');

    setSubmitting(true);
    try {
      await apiClient.post('/credit-requests', {
        store_id: user?.store_id,
        type: selectedType.id,
        customer_phone: phone.trim(),
        amount: Number(amount),
        reason: reason.trim(),
      });
      toast.success('Request submitted — awaiting SuperAdmin approval');
      resetForm();
      loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const statusBadge = (status) => {
    const map = {
      PENDING: 'bg-yellow-100 text-yellow-800',
      APPROVED: 'bg-green-100 text-green-800',
      REJECTED: 'bg-red-100 text-red-800',
    };
    return <Badge className={map[status] || ''}>{status}</Badge>;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        <Button variant="ghost" size="sm" onClick={() => navigate('/pos-dashboard')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-lg font-semibold">Wallet &amp; Loyalty Requests</h1>
      </div>

      <div className="max-w-2xl mx-auto p-4 space-y-4">

        {!selectedType && (
          <div className="grid grid-cols-2 gap-3">
            {REQUEST_TYPES.map((t) => (
              <Card
                key={t.id}
                className="cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => setSelectedType(t)}
              >
                <CardContent className="pt-6 flex flex-col items-center text-center gap-2">
                  <div className={`${t.color} p-3 rounded-lg`}>
                    <t.icon className="h-5 w-5 text-white" />
                  </div>
                  <span className="text-sm font-medium">{t.label}</span>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {selectedType && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <selectedType.icon className="h-4 w-4" /> {selectedType.label}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Customer phone</label>
                <div className="relative">
                  <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input
                    className="pl-9"
                    placeholder="10-digit phone number"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">
                  Amount ({selectedType.unit})
                </label>
                <Input
                  type="number"
                  min="0"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder={selectedType.unit === '₹' ? 'e.g. 85' : 'e.g. 150'}
                />
              </div>

              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Reason (mandatory)</label>
                <Textarea
                  rows={3}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Why is this adjustment needed?"
                />
              </div>

              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1" onClick={resetForm}>Cancel</Button>
                <Button className="flex-1" disabled={submitting} onClick={handleSubmit}>
                  {submitting ? 'Submitting...' : 'Submit for approval'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader><CardTitle className="text-sm">Your recent requests</CardTitle></CardHeader>
          <CardContent>
            {loadingHistory ? (
              <p className="text-sm text-gray-400">Loading...</p>
            ) : myRequests.length === 0 ? (
              <p className="text-sm text-gray-400">No requests yet.</p>
            ) : (
              <div className="space-y-2">
                {myRequests.slice(0, 10).map((r) => (
                  <div key={r.id} className="flex items-center justify-between text-sm border-b pb-2 last:border-0">
                    <div>
                      <div className="font-medium">{r.type.replace('_', ' ')} — {r.customer_phone}</div>
                      <div className="text-xs text-gray-400">{r.reason}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold">{r.amount}</div>
                      {statusBadge(r.status)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default WalletLoyaltyRequests;
